import React, { useRef, useEffect, useState, useCallback } from "react";
import { Card, Col, Collapse, Row } from "react-bootstrap";
import { Tabulator, PageModule, DownloadModule, ExportModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import { SingleSelectAsync } from "./SIngleSelectAsync";
import { SelectOption } from "./SIngleSelectAsync";

interface HighestPaidEmployeeByEntity {
  entity_id: number;
  emp_rank: number;
  fiscal_year: number;
  entity_name: string;
  employee_name: string;
  title: string;
  org1: string;
  wages: number;
  benefits: number;
  total: number;
}

interface EntityHighestPaidChartAndTableProps {
  selectedEntityId: number | null;
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
  data: HighestPaidEmployeeByEntity[];
  loading?: boolean;
  activeTab?: string;
  entityName?: string;
  fiscalYear: string | null;
  onFiscalYearChange?: (year: string | null) => void;
}

const EntityHighestPaidChartAndTable: React.FC<EntityHighestPaidChartAndTableProps> = ({
  selectedEntityId,
  formatCurrency,
  formatEntityString,
  data,
  loading,
  activeTab,
  entityName,
  fiscalYear,
  onFiscalYearChange
}) => {
  const years = ["2026","2025","2024","2023","2022","2021","2020","2019","2018","2017","2016","2015","2014"];
  const [options, setOptions] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<SelectOption | null>(
    fiscalYear ? { value: fiscalYear, label: fiscalYear } : null
  );
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);

  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);

  // Added to keep selectedYear in sync when the parent passes/updates fiscalYear after mount
  useEffect(() => {
    if (!fiscalYear) return;
    const fy = String(fiscalYear);
    if (!selectedYear || selectedYear.value !== fy) {
      const yearOption = { value: fy, label: fy };
      setSelectedYear(yearOption);
    }
  }, [fiscalYear]);

  const loadYearOptions = async (inputValue: string): Promise<SelectOption[]> => {
    return years
      .filter(year =>
        year.toLowerCase().includes(inputValue.toLowerCase())
      )
      .map(year => ({
        value: year,
        label: year,
      }));
  };

  useEffect(() => {
    if (!data.length) return;
    const chartData = data.map(d => ({ ...d, agg1: d.employee_name, net_amount: d.total }));
    const newOptions = Array.from(
      new Set(chartData.map(d => d.agg1).filter(d => d !== "NOT SPECIFIED"))
    ).sort();
    setOptions(newOptions);
    setSelectedOptions(newOptions);
  }, [data]);

  useEffect(() => {
    if (!data.length && !fiscalYear) return;

    const availableYears = Array.from(new Set(data.map(d => d.fiscal_year))).map(String);
    const mostRecentYear = availableYears.length ? String(Math.max(...availableYears.map(Number))) : null;

    const targetYear = fiscalYear && availableYears.includes(String(fiscalYear))
      ? String(fiscalYear)
      : mostRecentYear;

    if (!targetYear) return;

    if (!selectedYear || selectedYear.value !== targetYear) {
      const yearOption = { value: targetYear, label: targetYear };
      setSelectedYear(yearOption);
      onFiscalYearChange?.(targetYear);
    }
  }, [data, fiscalYear, onFiscalYearChange]);

  // Stable Tabulator creation/recreate function
  const createOrReplaceTable = useCallback(() => {
    if (!tableRef.current) return;
    if (!selectedYear) return;
    if (!data.length) return;
    if (!selectedOptions || selectedOptions.length === 0) return;
    if (activeTab !== "highestPaid") return;

    if (tabulatorRef.current) {
      try {
        tabulatorRef.current.destroy();
      } catch (e) {
      }
      tabulatorRef.current = null;
    }

    Tabulator.registerModule([PageModule, DownloadModule, ExportModule]);

    const mobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
      window.innerWidth < 1025;

    const filteredData = data
      .filter(d =>
        selectedOptions.includes(d.employee_name) &&
        d.fiscal_year.toString() === selectedYear.value
      )
      .sort((a, b) => b.fiscal_year - a.fiscal_year);

    tabulatorRef.current = new Tabulator(tableRef.current!, {
      layout: mobile ? "fitDataFill" : "fitColumns",
      pagination: "local",
      paginationSize: 10,
      columns: [
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-trophy'></i> Rank";
            return el;
          },
          field: "emp_rank",
          titleDownload: "Rank",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-calendar-alt'></i> Fiscal Year";
            return el;
          },
          field: "fiscal_year",
          titleDownload: "Fiscal Year",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
            return el;
          },
          field: "entity_name",
          titleDownload: "Entity",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-landmark'></i> Organization";
            return el;
          },
          field: "org1",
          titleDownload: "Organization",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-user'></i> Employee";
            return el;
          },
          field: "employee_name",
          titleDownload: "Employee",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-briefcase'></i> Title";
            return el;
          },
          field: "title",
          titleDownload: "Title",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-dollar-sign'></i> Wages";
            return el;
          },
          field: "wages",
          titleDownload: "Wages",
          formatter: (cell: any) => formatCurrency(cell.getValue()),
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-dollar-sign'></i> Benefits";
            return el;
          },
          field: "benefits",
          titleDownload: "Benefits",
          formatter: (cell: any) => formatCurrency(cell.getValue()),
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-dollar-sign'></i> Total";
            return el;
          },
          field: "total",
          titleDownload: "Total",
          formatter: (cell: any) => formatCurrency(cell.getValue()),
        },
      ],
      data: filteredData,
    });
  }, [selectedYear, data, selectedOptions, activeTab, formatCurrency]);

  // Initialize or replace table whenever relevant inputs change
  useEffect(() => {
    const timeout = setTimeout(() => {
      createOrReplaceTable();
    }, 0);

    const handleResize = () => {
      if (!tabulatorRef.current) return;
      tabulatorRef.current.setLayout(
        window.innerWidth < 1025 ? "fitDataFill" : "fitColumns"
      );
      tabulatorRef.current.redraw(true);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [loading, activeTab, data, selectedOptions, selectedYear, formatCurrency, createOrReplaceTable]);

  // Update table data without recreating when only the data/selection/year change and tab exists
  useEffect(() => {
    if (!selectedYear) return;

    const filteredData = data.filter(d =>
      selectedOptions.includes(d.employee_name) &&
      d.fiscal_year.toString() === selectedYear.value
    );
    
    if (tabulatorRef.current) {
      const timer = window.setTimeout(() => {
        try {
          tabulatorRef.current?.replaceData(filteredData);
        } catch (err) {
          try {
            createOrReplaceTable();
          } catch (e) {
          }
        }
      }, 50);

      return () => window.clearTimeout(timer);
    }

    try {
      createOrReplaceTable();
    } catch (e) {
    }
  }, [data, selectedOptions, selectedYear, selectedEntityId, createOrReplaceTable]);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {
    const formattedEntityName = entityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedEntityName}_highest_paid.${type}`);
  };

  // Added to clean up Tabulator when component unmounts
  useEffect(() => {
    return () => {
      if (tabulatorRef.current) {
        try {
          tabulatorRef.current.destroy();
        } catch (e) {
        }
        tabulatorRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <Row className="mb-2">
        <Col xs={12}>
          <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
            <Card.Body style={{ justifyItems: "center" }}>
              <div className="tu_tabHeader">
                <div className="tu-tabHeader__titleRow">
                  <h1 className="tu-tabHeader__title">
                    Highest Paid Employees</h1>
                    <button
                      type="button"
                      className="tu-tabHeader__infoBtn"
                      onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                      aria-label="Entity Highest Paid Employees info"
                    >
                      <i className="fas fa-info"/>
                    </button>
                </div>
              </div>

                <Collapse in={showHeaderInfo}>
                  <div>
                    <p className="lead mt-2 text-center">
                      The Highest Paid Employees tab allows you to search for the highest paid employees at an entity. The table displays the top 100 highest paid employees, with options to sort by name, title, wage, and other variables.
                    </p>
                  </div>
                </Collapse>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        // Loading
        <Card className="mb-4">
          <div className="p-5 text-center text-muted">
            <em>Loading...</em>
          </div>
        </Card>
      ) : data && data.length > 0 ? (
        // Data length > 0
        <Card className="mb-4">
          <Row className="py-3 px-4 no-overflow-row">
            <Col xs={12} md={4}>
              <span className="d-block mb-2">Select a Fiscal Year</span>
              <SingleSelectAsync
                value={selectedYear}
                onChange={(year) => {
                  setSelectedYear(year);
                  onFiscalYearChange?.(year?.value ?? null);
                }}
                isSearchable={true}
                type="year"
                loadOptions={loadYearOptions}
                defaultOptions={years.map(year => ({ value: year, label: year }))}
              />
            </Col>
          </Row>

          <Row className="py-4 px-4">
            <Col>
              <div className="mb-2 d-flex gap-2">
                <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
              </div>
              
              <div ref={tableRef} className="table-light" />
            </Col>
          </Row>
        </Card>
      ) : (
        // Not Loading/No Data
        <Card className="mb-4">
          <div className="p-5 text-center">
            <div className="alert alert-light shadow-sm border">
              No data available
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default EntityHighestPaidChartAndTable;